<template>
  <DefaultAuthLayout>
    <!-- Top Action Slot -->
    <template #topAction>
      <span class="text-gray mr-3 lg:inline">Já tem o código?</span>
      <router-link
        to="/account-activation"
        class="border border-stroke px-3 py-1 text-sm text-body transition-colors hover:bg-gray-2 lg:px-6 lg:py-2 lg:text-base">
        Ativar Conta
      </router-link>
    </template>

    <!-- Header Slot -->
    <template #header>
      <h2 class="mb-2 text-2xl font-bold text-black lg:text-3xl">{{ t('views.request_activation_token.title') }}</h2>
      <p class="text-sm text-body lg:text-base">{{ t('views.request_activation_token.description') }}</p>
    </template>

    <!-- Form Slot -->
    <template #form>
      <form @submit.prevent="handleSubmit" class="space-y-4 lg:space-y-6">
        <!-- Email Field -->
        <div class="mb-3 lg:mb-4">
          <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-black lg:text-sm">
            {{ t('views.request_activation_token.label.email') }}
          </label>
          <div class="relative">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 lg:pl-4">
              <IcEmail />
            </div>
            <input
              type="email"
              name="email"
              v-model="form.email"
              :placeholder="t('views.request_activation_token.placeholder.email')"
              class="w-full border border-stroke bg-transparent py-2.5 pl-10 pr-3 text-sm text-black outline-none transition-colors focus:border-blue focus:ring-2 focus:ring-blue lg:py-3 lg:pl-12 lg:pr-4 lg:text-base" />
          </div>
          <p v-if="errors.email" class="mt-1 text-xs text-danger">{{ errors.email }}</p>
        </div>

        <!-- Submit Button -->
        <div class="mb-3 mt-6 lg:mb-5 lg:mt-8">
          <button
            @click="handleSubmit"
            :disabled="isLoading"
            class="flex w-full cursor-pointer justify-center border border-primary bg-primary p-3 font-medium text-white transition hover:bg-blue disabled:cursor-not-allowed disabled:opacity-50 lg:p-4">
            <div class="flex flex-row items-center text-center">
              <div v-if="isLoading" class="mr-3 animate-spin items-center">
                <svg class="h-5 w-5 text-white lg:h-7 lg:w-7" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <span class="flex items-center justify-center text-base font-medium lg:text-lg">
                {{ isLoading ? 'Enviando...' : t('views.request_activation_token.action.submit') }}
              </span>
            </div>
          </button>
        </div>

        <!-- Info Message -->
        <div class="mt-4 text-center">
          <p class="text-xs text-body lg:text-sm">Você receberá um novo código de ativação por email.</p>
        </div>
      </form>
    </template>

    <!-- Additional Content Slot -->
    <template #additionalContent>
      <!-- Help Section -->
      <div class="mt-8 border-t border-stroke pt-6 text-center">
        <p class="mb-4 text-sm text-body">Precisa de ajuda?</p>
        <div class="space-y-2">
          <router-link to="/login" class="block text-sm font-medium text-blue hover:text-blue-2"> Voltar ao Login </router-link>
          <router-link to="/cadastro" class="block text-sm font-medium text-blue hover:text-blue-2"> Criar Nova Conta </router-link>
        </div>
      </div>
    </template>
  </DefaultAuthLayout>
</template>

<script setup lang="ts">
import DefaultAuthLayout from '@/components/Auths/DefaultAuthLayout.vue'
import { IcEmail } from '@/components/Icons'
import { IRequestActivationTokenParams } from '@/interfaces/api/user'
import RequestActivationTokenSchema from '@/schemas/RequestActivationTokenSchema'
import { useSuccessStore } from '@/store/success'
import { useUserStore } from '@/store/user'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ValidationError } from 'yup'

const { t } = useI18n()
const router = useRouter()
const { requestActivationToken } = useUserStore()

const isLoading = ref(false)

const form = ref({
  email: '',
})

const errors = ref({
  email: '',
})

const setErrors = (validationErrors: ValidationError) => {
  errors.value = {
    email: '',
  }

  validationErrors.inner.forEach((error) => {
    if (error.path) {
      errors.value[error.path as keyof typeof errors.value] = error.message
    }
  })
}

const requestUserAccountActivationToken = (values: IRequestActivationTokenParams) => {
  isLoading.value = true

  requestActivationToken(values, {
    onSuccess: (response: any) => {
      isLoading.value = false
      useSuccessStore().setSuccess([response.message])
      router.push({ name: 'account-activation' })
    },
    onFailure: () => {
      isLoading.value = false
    },
    onCompletion: () => {
      isLoading.value = false
    },
  })
}

const handleSubmit = () => {
  RequestActivationTokenSchema.validate(form.value, { abortEarly: false })
    .then(() => {
      requestUserAccountActivationToken(form.value)
    })
    .catch((err: ValidationError) => {
      setErrors(err)
    })
}
</script>
